/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useEffect, useMemo, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FormCheckbox,
  FormTextInput,
  FormSelect,
  FormRadioGroup
} from "protocol-setup/components/FormComponents";
import {
  FormButtonGroup,
  AddNewRowButton,
  FormButtonGroupHeader,
  ButtonActions
} from "protocol-setup/components/FormComponents/FormButtonGroup";
import { openSetup } from "protocol-setup/redux/constants";
import { putWithAuth } from "protocol-setup/redux/makeFetch";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import Text from "protocol-setup/components/Text";
import { openConfirmAlert } from "@ctsu/react-ctsu-ui";
import {
  OpenSuccessNotification,
  OpenErrorNotification
} from "protocol-setup/components/notification";
import { FarQuestionCircle, FasTrashAlt } from "assets/icons";
import {
  getUserFriendlyError,
  mapDefaultValues,
  removeElementReconfirm
} from "protocol-setup/helpers";
import { titleAndAria } from "protocol-setup/constants/errorMessages";
import { formSchema } from "./openSetupSchema";
import {
  constraintMappings,
  handleAncillaryUndo,
  handleIrocUndo,
  handleStepUndo,
  regTypetoolTip,
  tabNames
} from "./orHelpers";
import {
  standardRandoNodeUrlOptions,
  embeddedAncillaryStudyOptions,
  mandatoryOptionalOptions,
  slotReservationProtocolOptions,
  irocCredentialingOptions,
  requiredOptionalOptions,
  stepRequiredOptions,
  registrationTypeOptions,
  patientInitializationOptions,
  transferEcDataOptions
} from "./Lovs";



const onSubmit = async (data, selectedRequest, onSuccess) => {
  const payload = data;
  payload.protocolNumber = selectedRequest.protocolNumber;
  payload.requestNumber = selectedRequest.requestNumber;

  try {
    const { psOpenId } = await putWithAuth(openSetup, payload);
    onSuccess({ ...selectedRequest, psOpenId });
    OpenSuccessNotification({
      message:"Record successfully updated.",
      autoHide: true
    });
  } catch (error) {
    const message = getUserFriendlyError(error, constraintMappings);
    OpenErrorNotification({
      message,
      autoHide: true
    });
  }
};

export const OpenSetupFormCore = ({
  defaultValues,
  selectedRequest,
  isFormReadOnly,
  isSubmitFormEnabled,
  fetchPsData,
  setDirtyTab
}) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { isDirty, dirtyFields }
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues
  });

  const {
    fields: ancillaryFields,
    append: appendAncillary,
    remove: removeAncillary
  } = useFieldArray({
    control,
    name: "protSetupAncillaryStudies"
  });

  const {
    fields: irocFields,
    append: appendIroc,
    remove: removeIroc
  } = useFieldArray({
    control,
    name: "protSetupIrocDetails"
  });

  const {
    fields: protSetupStepFields,
    append: appendStep,
    remove: removeStep
  } = useFieldArray({
    control,
    name: "protSetupStepDetails"
  });

   const ancillaryRef = useRef(null);
   const irocRef = useRef(null);
   const stepRef = useRef(null);
   const addStepRef = useRef(null);
   const addIrocRef = useRef(null);
   const addAncillaryRef = useRef(null);

  const showRandoNodeUrl =
    typeof watch("isStandardRandoNodeUrl") === "boolean" &&
    !watch("isStandardRandoNodeUrl");
  const showAncillaryGrid = watch("isEmbeddedAncillaryStudy");
  const showIrocGrid = watch("isIrocCredentialingInfoOpen");
  const showSlotSteps = watch("isSlotReservationProtocol");
  const memoizedSelectedRequest = useMemo(
    () => selectedRequest,
    [selectedRequest]
  );

  const formTitle = "OPEN Setup";

  const addAncillaryStudy = () => {
    appendAncillary({
      groupProtocolNumber: "",
      stepNumber: null,
      mandatory: "MANDATORY"
    });
  };

  const addIrocType = () => {
    appendIroc({
      type: "",
      requirement: "MANDATORY"
    });
  };
  // a. Required =''
  //   b. Registration Type
  //   c. Patient Initialization
  //   d. Transfer EC Data
  const addStep = () => {
    appendStep({
      required: "",
      publicFormIds: "",
      crfVersionNum: "",
      stepNumber: "",
      stepDescription: "",
      registrationType: "",
      initialization: "",
      transferEcData: "",
      markForDownload: true
    });
  };

  const resetForm = () => reset(defaultValues);
  useEffect(() => {
    setDirtyTab(isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (memoizedSelectedRequest) {
      const newValues = mapDefaultValues(
        memoizedSelectedRequest,
        defaultValues,
        {}
      );
      reset(newValues);
    }
  }, [memoizedSelectedRequest, reset]);

  const onSuccess = async () => {
    await fetchPsData();
  };

  // Wrap onSubmit with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback, event) =>
      onSubmit(data, onSuccessCallback, event),
    selectedRequest?.psOpenId ? dirtyFields :  {"initialSave":true},
    tabNames.OPEN_SETUP,
    selectedRequest?.psOpenId ? "Updates" : "Initial Save"
  );

  return (
    <div
      className={`form-layout-wrapper my-3 ${
        isFormReadOnly ? "read-only" : ""
      }`}
    >
      <form
        onSubmit={handleSubmit((data) =>
          onSubmitWithHistory(data, selectedRequest, onSuccess)
        )}
        className="d-block my-3"
      >
        <div className="form-layout-header">
          {isFormReadOnly ? (
            <></>
          ) : (
            <FormButtonGroupHeader
              control={control}
              readOnly={isFormReadOnly}
              reset={resetForm}
              formTitle={formTitle}
            />
          )}
          <div className="form-layout-title invisible">
            Protocol Setup, OPEN
          </div>
        </div>
        <div
          className={`form-layout-body p-0 ${
            isFormReadOnly ? "pointer-events-none" : ""
          }`}
        >
          <div className="invisible"></div>
          <ul>
            <li
              role="group"
              className="form-sub-group d-block"
            >
              <div
                id="form-subgroup1"
                className="form-sub-group-label invisible"
              >
                Rando/Node Information
              </div>
              <div className="row">
                <div className="d-block col-4 mr-4">
                  <FormRadioGroup
                    name="isStandardRandoNodeUrl"
                    section="openRave"
                    form="openSetupForm"
                    control={control}
                    readOnly={isFormReadOnly}
                    onChangeCallBack={(value) => {
                      if (value) {
                        setValue("randoNodeUrl", "");
                      }
                    }}
                    options={standardRandoNodeUrlOptions}
                    required
                  />
                </div>
                {showRandoNodeUrl && (
                  <>
                    <div className="d-block col-4 mr-4">
                      <FormTextInput
                        name="randoNodeUrl"
                        section="openRave"
                        form="openSetupForm"
                        control={control}
                        readOnly={isFormReadOnly}
                        required
                        trigger={trigger}
                      />
                    </div>
                    <div className="d-block col-4" />
                  </>
                )}
              </div>
            </li>
            <li
              role="group"
              className="form-sub-group d-block"
            >
              <div
                id="form-subgroup2"
                className="form-sub-group-label invisible"
              >
                Embedded Ancillary Study
              </div>
              <div className="row">
                <div className="d-block col-4 mr-4">
                  <FormRadioGroup
                    name="isEmbeddedAncillaryStudy"
                    section="openRave"
                    form="openSetupForm"
                    control={control}
                    readOnly={isFormReadOnly}
                    onChangeCallBack={(value) => {
                      if (!value) {
                        setValue("protSetupAncillaryStudies", []);
                      } else if (ancillaryFields.length === 0) {
                        appendAncillary({
                          psOpenAncillaryId: null,
                          groupProtocolNumber: "",
                          stepNumber: null,
                          mandatory: "MANDATORY"
                        });
                      }
                    }}
                    options={embeddedAncillaryStudyOptions}
                    required
                  />
                </div>
                {showAncillaryGrid && (
                  <div className="d-block col-8 ml-n3">
                    <div className="form-label">
                      <span className="form-layout-required">*</span>
                      <Text bundle="openRave.openSetupForm.protSetupAncillaryStudies.sectionLabel" />
                    </div>
                    <div>
                      <AddNewRowButton
                        onClick={addAncillaryStudy}
                        text="Study"
                        title={titleAndAria.addStudy}
                        refForward={addAncillaryRef}
                      />
                    </div>

                    <div>
                      {ancillaryFields.map((field, index) => (
                        <div
                          key={field.id}
                          data-ancillary-id={field?.psOpenAncillaryId}
                          className={`row pb-2 ${index > 0 && "pt-2"} formRow__bottom--grey`}
                        >
                          <div className="col-4">
                            <div className="form-layout-input">
                              <FormTextInput
                                section="openRave"
                                form="openSetupForm"
                                hideLabel={index > 0}
                                labelClassName="font-bold pl-2"
                                required
                                name={`protSetupAncillaryStudies.${index}.groupProtocolNumber`}
                                control={control}
                                readOnly={isFormReadOnly}
                                className="mt-1 bg-white"
                                trigger={trigger}
                                rowPosition="first"
                              />
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="form-layout-input">
                              <FormTextInput
                                name={`protSetupAncillaryStudies.${index}.stepNumber`}
                                control={control}
                                readOnly={isFormReadOnly}
                                section="openRave"
                                form="openSetupForm"
                                labelClassName="font-bold pl-2"
                                required
                                type="number"
                                min={1}
                                max={99}
                                hideLabel={index > 0}
                                className="mt-1 bg-white"
                                trigger={trigger}
                                rowPosition
                              />
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="form-layout-input">
                              <FormSelect
                                name={`protSetupAncillaryStudies.${index}.mandatory`}
                                control={control}
                                readOnly={isFormReadOnly}
                                section="openRave"
                                form="openSetupForm"
                                hideLabel={index > 0}
                                labelClassName="font-bold pl-2"
                                className="bg-white"
                                rowPosition
                                options={mandatoryOptionalOptions}
                              />
                            </div>
                          </div>
                          <div>
                            <ButtonActions
                              index={index}
                              forwardRef={ancillaryRef}
                              onDelete={() => {
                                openConfirmAlert({
                                  title: (
                                    <div className="d-flex align-items-center">
                                      <FasTrashAlt className="mr-2 color-red" />
                                      <span>Delete Row</span>
                                    </div>
                                  ),
                                  message: titleAndAria.deleteRowConfirmation,
                                  buttons: [
                                    {
                                      label: "Confirm",
                                      onClick: () => {
                                        removeAncillary(index);
                                        setTimeout(() => {
                                          addAncillaryRef.current.focus();
                                        }, 0);
                                      },
                                      className: ""
                                    },
                                    {
                                      label: "Cancel",
                                      onClick: () => {
                                        removeElementReconfirm()
                                        setTimeout(() => {
                                          ancillaryRef.current.focus();
                                        }, 0);
                                      },
                                      className: "close"
                                    }
                                  ],
                                  closeOnEscape: true,
                                  closeOnClickOutside: true
                                });
                              }}
                              title={titleAndAria.deleteRow}
                              watch={watch}
                              fieldName={`protSetupAncillaryStudies.${index}`}
                              control={control}
                              undoTitle={titleAndAria.undoTitle("changes")}
                              onUndo={() =>
                                handleAncillaryUndo(
                                  field,
                                  index,
                                  defaultValues,
                                  setValue
                                )}
                              disabled={ancillaryFields.length < 2}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </li>
            <li
              role="group"
              className="form-sub-group d-block"
            >
              <div
                id="form-subgroup3"
                className="form-sub-group-label invisible"
              >
                Slot Reservation
              </div>
              <div className="row">
                <div className="d-block col-4 mr-4">
                  <FormRadioGroup
                    name="isSlotReservationProtocol"
                    section="openRave"
                    form="openSetupForm"
                    control={control}
                    readOnly={isFormReadOnly}
                    options={slotReservationProtocolOptions}
                    required
                    onChangeCallBack={(value) => {
                      if (!value) {
                        setValue("slotReservationSteps", "");
                      }
                    }}
                  />
                </div>
                {showSlotSteps && (
                  <>
                    <div className="d-block col-4 mr-4">
                      <FormTextInput
                        name="slotReservationSteps"
                        section="openRave"
                        form="openSetupForm"
                        control={control}
                        readOnly={isFormReadOnly}
                        required
                        trigger={trigger}
                      />
                    </div>
                    <div className="d-block col-4" />
                  </>
                )}
              </div>
            </li>
            <li
              role="group"
              className="form-sub-group d-block"
            >
              <div
                id="form-subgroup4"
                className="form-sub-group-label invisible"
              >
                IROC Credentialing
              </div>
              <div className="row">
                <div className="d-block col-4 mr-4">
                  <FormRadioGroup
                    name="isIrocCredentialingInfoOpen"
                    section="openRave"
                    form="openSetupForm"
                    control={control}
                    readOnly={isFormReadOnly}
                    onChangeCallBack={(value) => {
                      if (value === false) {
                        setValue("protSetupIrocDetails", []);
                      } else if (irocFields.length === 0) {
                        appendIroc({ type: "", requirement: "MANDATORY" });
                      }
                    }}
                    options={irocCredentialingOptions}
                    required
                  />
                </div>
                {showIrocGrid && (
                  <div className="d-block col-8 ml-n3">
                    <div className="form-label">
                      <span className="form-layout-required">*</span>
                      <Text bundle="openRave.openSetupForm.protSetupIrocDetails.sectionLabel" />
                    </div>
                    <div>
                      <AddNewRowButton
                        onClick={addIrocType}
                        text="Type"
                        title={titleAndAria.addType}
                        readOnly={isFormReadOnly}
                        refForward={addIrocRef}
                      />
                    </div>
                    <div>
                      {irocFields.map((field, index) => (
                        <div
                          key={field.id}
                          data-ancillary-id={field?.psOpenIrocId}
                          className={`row pb-2 ${index > 0 && "pt-2"} formRow__bottom--grey`}
                        >
                          <div className="col-6">
                            <div className="form-layout-input">
                              <FormTextInput
                                name={`protSetupIrocDetails.${index}.type`}
                                control={control}
                                readOnly={isFormReadOnly}
                                required
                                hideLabel={index > 0}
                                section="openRave"
                                form="openSetupForm"
                                labelClassName="font-bold pl-2"
                                className="mt-1 bg-white"
                                trigger={trigger}
                                rowPosition="first"
                              />
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="form-layout-input">
                              <FormSelect
                                name={`protSetupIrocDetails.${index}.requirement`}
                                control={control}
                                readOnly={isFormReadOnly}
                                section="openRave"
                                form="openSetupForm"
                                hideLabel={index > 0}
                                labelClassName="font-bold pl-2"
                                className="bg-white"
                                rowPosition
                                options={requiredOptionalOptions}
                              />
                            </div>
                          </div>
                          <div data-index={index}>
                            <ButtonActions
                              index={index}
                              forwardRef={irocRef}
                              onDelete={() => {
                                openConfirmAlert({
                                  title: (
                                    <div className="d-flex align-items-center">
                                      <FasTrashAlt className="mr-2 color-red" />
                                      <span>Delete Row</span>
                                    </div>
                                  ),
                                  message: titleAndAria.deleteRowConfirmation,
                                  buttons: [
                                    {
                                      label: "Confirm",
                                      onClick: () => {
                                        removeIroc(index);
                                        setTimeout(() => {
                                          addIrocRef.current.focus();
                                        }, 0);
                                      },
                                      className: ""
                                    },
                                    {
                                      label: "Cancel",
                                      onClick: () => {
                                        removeElementReconfirm();
                                        setTimeout(() => {
                                          ancillaryRef.current.focus();
                                        }, 0);
                                      },
                                      className: "close"
                                    }
                                  ],
                                  closeOnEscape: true,
                                  closeOnClickOutside: true
                                });
                              }}
                              title={titleAndAria.deleteRow}
                              watch={watch}
                              fieldName={`protSetupIrocDetails.${index}`}
                              control={control}
                              undoTitle={titleAndAria.undoTitle("changes")}
                              onUndo={() =>
                                handleIrocUndo(
                                  field,
                                  index,
                                  defaultValues,
                                  setValue
                                )}
                              disabled={irocFields.length < 2}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </li>
            <li className="d-block col-12 px-3">
              <div className="form-label color-dark-blue mr-3">
                <span className="form-layout-required">*</span>
                <Text bundle="openRave.openSetupForm.protSetupStepDetails.sectionLabel" />
              </div>
              <p className="m-1">
                <Text bundle="openRave.openSetupForm.protSetupStepDetails.sectionDescription" />
              </p>
              <div>
                <AddNewRowButton
                  onClick={addStep}
                  text="Step"
                  title={titleAndAria.addStep}
                  readOnly={isFormReadOnly}
                  refForward={addStepRef}
                />
              </div>
              <div className="d-table" style={{ borderCollapse: "collapse", tableLayout: 'fixed', width: '100%' }}>
                {protSetupStepFields.map((field, index) => (
                  <div
                    key={field.id}
                    data-open-step-id={field?.psOpenStepId}
                    className={`row d-table-row pb-2 ${index > 0 && "pt-2"} formRow__bottom--grey`}
                  >
                    <div
                      className="d-table-cell pb-2"
                      style={{ verticalAlign: "bottom" }}
                    >
                      <div className="form-layout-input mr-2">
                        <FormSelect
                          name={`protSetupStepDetails.${index}.required`}
                          control={control}
                          readOnly={isFormReadOnly}
                          section="openRave"
                          form="openSetupForm"
                          hideLabel={index > 0}
                          labelClassName="font-bold "
                          index={index}
                          required
                          options={stepRequiredOptions}
                        />
                      </div>
                    </div>
                    <div
                      className="d-table-cell pb-2"
                      style={{ verticalAlign: "bottom" }}
                    >
                      <div className="form-layout-input mr-2">
                        <FormTextInput
                          name={`protSetupStepDetails.${index}.publicFormIds`}
                          control={control}
                          readOnly={isFormReadOnly}
                          section="openRave"
                          form="openSetupForm"
                          className="mt-1"
                          hideLabel={index > 0}
                          labelClassName="font-bold "
                          required
                          index={index}
                          trigger={trigger}
                        />
                      </div>
                    </div>
                    <div
                      className="d-table-cell pb-2"
                      style={{ verticalAlign: "bottom" }}
                    >
                      <div className="form-layout-input mr-2">
                        <FormTextInput
                          name={`protSetupStepDetails.${index}.crfVersionNum`}
                          control={control}
                          readOnly={isFormReadOnly}
                          section="openRave"
                          form="openSetupForm"
                          className="mt-1"
                          hideLabel={index > 0}
                          labelClassName="font-bold "
                          required
                          index={index}
                          trigger={trigger}
                        />
                      </div>
                    </div>
                    <div
                      className="d-table-cell pb-2"
                      style={{ verticalAlign: "bottom" }}
                    >
                      <div className="form-layout-input mr-2">
                        <FormTextInput
                          name={`protSetupStepDetails.${index}.stepNumber`}
                          control={control}
                          readOnly={isFormReadOnly}
                          section="openRave"
                          form="openSetupForm"
                          hideLabel={index > 0}
                          labelClassName="font-bold "
                          required
                          index={index}
                          className="mt-1"
                          trigger={trigger}
                        />
                      </div>
                    </div>
                    <div
                      className="d-table-cell pb-2"
                      style={{ verticalAlign: "bottom" }}
                    >
                      <div className="form-layout-input mr-2">
                        <FormTextInput
                          name={`protSetupStepDetails.${index}.stepDescription`}
                          control={control}
                          readOnly={isFormReadOnly}
                          section="openRave"
                          form="openSetupForm"
                          hideLabel={index > 0}
                          labelClassName="font-bold "
                          className="mt-1"
                          required
                          index={index}
                          trigger={trigger}
                        />
                      </div>
                    </div>
                    <div
                      className="d-table-cell pb-2"
                      style={{ verticalAlign: "bottom" }}
                    >
                      <div className="form-layout-input mr-2">
                        <FormSelect
                          name={`protSetupStepDetails.${index}.registrationType`}
                          control={control}
                          readOnly={isFormReadOnly}
                          label={
                            <>
                              Registration <br /> Type
                              <span
                                title={regTypetoolTip()}
                                className="lnk"
                                tabIndex="0"
                                role="tooltip"
                              >
                                <FarQuestionCircle />
                              </span>
                            </>
                          }
                          hideLabel={index > 0}
                          labelClassName="font-bold "
                          required
                          index={index}
                          options={registrationTypeOptions}
                        />
                      </div>
                    </div>
                    <div
                      className="d-table-cell pb-2"
                      style={{ verticalAlign: "bottom" }}
                    >
                      <div className="form-layout-input mr-2">
                        <FormSelect
                          name={`protSetupStepDetails.${index}.initialization`}
                          control={control}
                          readOnly={isFormReadOnly}
                          label="Patient Initialization"
                          hideLabel={index > 0}
                          labelClassName="font-bold "
                          required
                          index={index}
                          options={patientInitializationOptions}
                        />
                      </div>
                    </div>
                    <div
                      className="d-table-cell pb-2"
                      style={{ verticalAlign: "bottom" }}
                    >
                      <div className="form-layout-input mr-2">
                        <FormSelect
                          name={`protSetupStepDetails.${index}.transferEcData`}
                          control={control}
                          readOnly={isFormReadOnly}
                          label="Transfer EC Data"
                          hideLabel={index > 0}
                          labelClassName="font-bold "
                          required
                          options={transferEcDataOptions}
                        />
                      </div>
                    </div>
                    <div
                      className="d-table-cell pb-2"
                      style={{ verticalAlign: "bottom", width: '6rem' }}
                    >
                      <div className="form-check mr-2">
                        {index === 0 && (
                          <div className="color-dark-blue mr-3 font-bold mb-n1">
                            Mark for Download
                          </div>
                        )}
                        <div className="pt-2 mt-1" style={{ height: '2.5rem' }}>
                          <FormCheckbox
                            name={`protSetupStepDetails.${index}.markForDownload`}
                            control={control}
                            readOnly={isFormReadOnly}
                            label="Mark for Download"
                            hideLabel
                            index={index}
                            wrapperClassName={isFormReadOnly ? 'read-only-show' : ''}
                            disabled={isFormReadOnly}
                          />
                        </div>
                      </div>
                    </div>
                    <div
                      className="d-table-cell pb-2"
                      style={{ verticalAlign: "bottom", width: '4.25rem' }}
                    >
                      {index === 0 && (
                        <div className="color-dark-blue mr-3 font-bold mb-n1">
                          &nbsp;
                        </div>
                      )}
                      <div className="mt-2 mb-n1">
                        <ButtonActions
                          index={index + 1}
                          watch={watch}
                          fieldName={`protSetupStepDetails.${index}`}
                          control={control}
                          forwardRef={stepRef}
                          undoTitle={titleAndAria.undoTitle("changes")}
                          onDelete={() => {
                            openConfirmAlert({
                              title: (
                                <div className="d-flex align-items-center">
                                  <FasTrashAlt className="mr-2 color-red" />
                                  <span>Delete Row</span>
                                </div>
                              ),
                              message: titleAndAria.deleteRowConfirmation,
                              buttons: [
                                {
                                  label: "Confirm",
                                  onClick: () => {
                                    if (protSetupStepFields.length > 1) {
                                      removeStep(index);
                                      setTimeout(() => {
                                        addStepRef.current.focus();
                                      }, 0);
                                    }
                                  },
                                  className: ""
                                },
                                {
                                  label: "Cancel",
                                  onClick: () => {
                                    removeElementReconfirm();
                                    setTimeout(() => {
                                      stepRef.current.focus();
                                    }, 0);
                                  },
                                  className: "close"
                                }
                              ],
                              closeOnEscape: true,
                              closeOnClickOutside: true
                            });
                          }}
                          onUndo={() =>
                            handleStepUndo(
                              field,
                              index,
                              defaultValues,
                              setValue,
                              getValues
                            )}
                          title={titleAndAria.deleteRow}
                          disabled={protSetupStepFields.length < 2}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </li>
            <li className="d-block col-4 mr-4 px-3">
              <FormTextInput
                name="requestedTestSites"
                section="openRave"
                form="openSetupForm"
                control={control}
                readOnly={isFormReadOnly}
                required
                trigger={trigger}
              />
            </li>
            <li className="d-block col-8 mr-4 px-3 mb-3">
              <FormTextInput
                name="additionalSetupNotes"
                section="openRave"
                form="openSetupForm"
                control={control}
                readOnly={isFormReadOnly}
                trigger={trigger}
              />
            </li>
          </ul>
        </div>

        <div className="form-layout-footer">
          {isFormReadOnly ? (
            <></>
          ) : (
            <FormButtonGroup
              control={control}
              readOnly={isFormReadOnly}
              reset={resetForm}
              formTitle={formTitle}
              requestNumber={selectedRequest?.requestNumber}
              formPartType={selectedRequest?.formPartType}
              handleSubmit={handleSubmit}
              isSubmitFormEnabled={isSubmitFormEnabled}
              onSuccess={onSuccess}
            />
          )}
        </div>
      </form>
    </div>
  );
};

const OpenSetupForm = ({
  selectedRequest,
  requestNumber = "",
  isFormReadOnly,
  fetchPsData,
  isSubmitFormEnabled,
  setIsSubmitFormEnabled,
  setDirtyTab
}) => {
  const fieldConfig = {
    psOpenId: null,
    protocolNumber: "",
    isStandardRandoNodeUrl: "",
    randoNodeUrl: "",
    isEmbeddedAncillaryStudy: "",
    protSetupAncillaryStudies: [],
    isSlotReservationProtocol: "",
    slotReservationSteps: "",
    isIrocCredentialingInfoOpen: "",
    protSetupIrocDetails: [],
    requestNumber: "",
    protSetupStepDetails: [
      {
        required: "",
        publicFormIds: "",
        crfVersionNum: "",
        stepNumber: "",
        stepDescription: "",
        registrationType: "",
        initialization: "",
        transferEcData: "",
        markForDownload: true
      }
    ],
    requestedTestSites: "",
    additionalSetupNotes: ""
  };

  const defaultValues = mapDefaultValues(selectedRequest, fieldConfig, {
    requestNumber: selectedRequest?.requestNumber || requestNumber || "",
    protocolNumber:
      selectedRequest?.protocol || selectedRequest?.protocolNumber || ""
  });
  return (
    <OpenSetupFormCore
      defaultValues={defaultValues}
      selectedRequest={selectedRequest}
      isFormReadOnly={isFormReadOnly}
      fetchPsData={fetchPsData}
      isSubmitFormEnabled={isSubmitFormEnabled}
      setIsSubmitFormEnabled={setIsSubmitFormEnabled}
      setDirtyTab={setDirtyTab}
    />
  );
};

export default OpenSetupForm;
